import { Link } from 'react-router-dom'
import {
  BarChart3,
  Search,
  TrendingUp,
  Zap,
  Activity,
  CheckCircle,
  ArrowRight,
  Target,
  Users,
  Shield,
  Mail,
  Eye,
  Calendar,
  BookOpen
} from 'lucide-react'
import MetricCard, { MetricCardGroup } from '../components/MetricCard'
import { useState, useEffect } from 'react'
import ContactFormModal from '../components/ContactFormModal'
import {
  siAnthropic,
  siGooglegemini,
  siPerplexity,
  siX,
  siMeta
} from 'simple-icons'
import { getAllPosts, formatDate } from '../lib/blog'
import { SEO, createWebsiteJsonLd, createOrganizationJsonLd } from '../components/SEO'

export default function Home() {
  const [isContactModalOpen, setIsContactModalOpen] = useState(false)
  const [showStickyBar, setShowStickyBar] = useState(true)
  const [lastScrollY, setLastScrollY] = useState(0)

  // Get latest 3 blog posts
  const latestPosts = getAllPosts().slice(0, 3)

  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY

      // Show bar when scrolling up, hide when scrolling down
      if (currentScrollY < lastScrollY || currentScrollY < 100) {
        setShowStickyBar(true)
      } else if (currentScrollY > lastScrollY && currentScrollY > 100) {
        setShowStickyBar(false)
      }

      setLastScrollY(currentScrollY)
    }

    window.addEventListener('scroll', handleScroll, { passive: true })

    return () => {
      window.removeEventListener('scroll', handleScroll)
    }
  }, [lastScrollY])

  // Combine JSON-LD schemas
  const jsonLd = [
    createWebsiteJsonLd(),
    createOrganizationJsonLd()
  ]

  return (
    <>
      <SEO
        title="BrandIndex AI - Track Your Brand's AI Visibility"
        description="Monitor and optimize how your brand appears in AI assistants like ChatGPT, Claude, and Gemini. Get actionable insights to improve your AI search visibility."
        canonical="https://brandindex.ai"
        jsonLd={jsonLd}
      />
      <style>{`
        /* Range Slider Styling */
        input[type="range"].slider {
          -webkit-appearance: none;
          appearance: none;
          height: 12px;
          border-radius: 6px;
          outline: none;
        }

        /* Webkit browsers (Chrome, Safari) */
        input[type="range"].slider::-webkit-slider-thumb {
          -webkit-appearance: none;
          appearance: none;
          width: 28px;
          height: 28px;
          border-radius: 50%;
          background: linear-gradient(135deg, #6366f1 0%, #a855f7 100%);
          cursor: pointer;
          box-shadow: 0 4px 12px rgba(99, 102, 241, 0.4);
          border: 3px solid white;
          transition: transform 0.2s, box-shadow 0.2s;
        }

        input[type="range"].slider::-webkit-slider-thumb:hover {
          transform: scale(1.15);
          box-shadow: 0 6px 16px rgba(99, 102, 241, 0.5);
        }

        input[type="range"].slider::-webkit-slider-thumb:active {
          transform: scale(1.05);
          box-shadow: 0 2px 8px rgba(99, 102, 241, 0.6);
        }

        /* Firefox */
        input[type="range"].slider::-moz-range-thumb {
          width: 28px;
          height: 28px;
          border-radius: 50%;
          background: linear-gradient(135deg, #6366f1 0%, #a855f7 100%);
          cursor: pointer;
          box-shadow: 0 4px 12px rgba(99, 102, 241, 0.4);
          border: 3px solid white;
          transition: transform 0.2s, box-shadow 0.2s;
        }

        input[type="range"].slider::-moz-range-thumb:hover {
          transform: scale(1.15);
          box-shadow: 0 6px 16px rgba(99, 102, 241, 0.5);
        }

        input[type="range"].slider::-moz-range-thumb:active {
          transform: scale(1.05);
          box-shadow: 0 2px 8px rgba(99, 102, 241, 0.6);
        }

        /* Firefox track */
        input[type="range"].slider::-moz-range-track {
          height: 12px;
          border-radius: 6px;
        }
      `}</style>
      <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-purple-50">
      {/* Hero Section */}
      <div className="container mx-auto px-4 py-8 md:py-16">
        <div className="max-w-4xl mx-auto text-center">
          <div className="inline-flex gap-3 items-center bg-indigo-100 px-4 py-2 rounded-full mb-6">
            <TrendingUp className="h-5 w-5 text-indigo-600" />
            <p className="text-md text-indigo-700">Welcome to <span className="font-bold">BrandIndex.ai</span></p>
          </div>

          <h1 className="text-5xl md:text-6xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-indigo-600 to-purple-600 mb-6">
            <span className="block text-2xl text-gray-900 mb-2">AI-Powered Brand Intelligence</span>
            Own Your Brand's AI Visibility
          </h1>

          <p className="text-2xl text-gray-700 mb-4 max-w-3xl mx-auto leading-relaxed">
            Go beyond vanity metrics. Understand <strong>how consumers search</strong>, <strong>what questions they ask</strong>, and <strong>which brands they discover</strong> across AI assistants, search engines, and citation sources.
          </p>

          <p className="text-lg text-gray-500 mb-8 max-w-2xl mx-auto">
            When customers ask ChatGPT, Claude, or Gemini for recommendations, find out if you're in the answer — and what to do if you're not.
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            <Link
              to="/register"
              className="bg-gradient-to-r from-indigo-500 to-purple-500 text-white px-8 py-4 rounded-lg font-semibold text-lg hover:from-indigo-600 hover:to-purple-600 transition-all shadow-lg hover:shadow-xl flex items-center"
            >
              Start Free Trial
              <ArrowRight className="ml-2 h-5 w-5" />
            </Link>
            <Link
              to="/login"
              className="text-indigo-600 px-8 py-4 rounded-lg font-semibold text-lg hover:text-indigo-700 transition-all"
            >
              Sign In
            </Link>
          </div>

          <p className="text-sm text-gray-500 mt-4">
            7-day free trial • No credit card required • Cancel anytime
          </p>
          <p className="text-sm text-purple-600 font-medium mt-2 flex items-center justify-center gap-2">
            <ArrowRight className="h-4 w-4" />
            Try the live demo - no signup required
          </p>

          {/* AI Provider Logos */}
          <div className="mt-6">
            <p className="text-xs text-gray-500 mb-3">Optimize your visibility across all major AI assistants:</p>
            <div className="flex flex-wrap items-center justify-center gap-2">
              {/* ChatGPT - Custom icon */}
              <div className="flex items-center gap-2 bg-white border border-gray-200 px-3 py-1.5 rounded-full shadow-sm">
                <svg viewBox="0 0 24 24" className="w-3.5 h-3.5" fill="#10a37f">
                  <path d="M22.2819 9.8211a5.9847 5.9847 0 0 0-.5157-4.9108 6.0462 6.0462 0 0 0-6.5098-2.9A6.0651 6.0651 0 0 0 4.9807 4.1818a5.9847 5.9847 0 0 0-3.9977 2.9 6.0462 6.0462 0 0 0 .7427 7.0966 5.98 5.98 0 0 0 .511 4.9107 6.051 6.051 0 0 0 6.5146 2.9001A5.9847 5.9847 0 0 0 13.2599 24a6.0557 6.0557 0 0 0 5.7718-4.2058 5.9894 5.9894 0 0 0 3.9977-2.9001 6.0557 6.0557 0 0 0-.7475-7.0729zm-9.022 12.6081a4.4755 4.4755 0 0 1-2.8764-1.0408l.1419-.0804 4.7783-2.7582a.7948.7948 0 0 0 .3927-.6813v-6.7369l2.02 1.1686a.071.071 0 0 1 .038.052v5.5826a4.504 4.504 0 0 1-4.4945 4.4944zm-9.6607-4.1254a4.4708 4.4708 0 0 1-.5346-3.0137l.142.0852 4.783 2.7582a.7712.7712 0 0 0 .7806 0l5.8428-3.3685v2.3324a.0804.0804 0 0 1-.0332.0615L9.74 19.9502a4.4992 4.4992 0 0 1-6.1408-1.6464zM2.3408 7.8956a4.485 4.485 0 0 1 2.3655-1.9728V11.6a.7664.7664 0 0 0 .3879.6765l5.8144 3.3543-2.0201 1.1685a.0757.0757 0 0 1-.071 0l-4.8303-2.7865A4.504 4.504 0 0 1 2.3408 7.872zm16.5963 3.8558L13.1038 8.364 15.1192 7.2a.0757.0757 0 0 1 .071 0l4.8303 2.7913a4.4944 4.4944 0 0 1-.6765 8.1042v-5.6772a.79.79 0 0 0-.407-.667zm2.0107-3.0231l-.142-.0852-4.7735-2.7818a.7759.7759 0 0 0-.7854 0L9.409 9.2297V6.8974a.0662.0662 0 0 1 .0284-.0615l4.8303-2.7866a4.4992 4.4992 0 0 1 6.6802 4.66zM8.3065 12.863l-2.02-1.1638a.0804.0804 0 0 1-.038-.0567V6.0742a4.4992 4.4992 0 0 1 7.3757-3.4537l-.142.0805L8.704 5.459a.7948.7948 0 0 0-.3927.6813zm1.0976-2.3654l2.602-1.4998 2.6069 1.4998v2.9994l-2.5974 1.4997-2.6067-1.4997Z"/>
                </svg>
                <span className="text-xs font-medium text-gray-700">ChatGPT</span>
              </div>
              {/* Claude */}
              <div className="flex items-center gap-2 bg-white border border-gray-200 px-3 py-1.5 rounded-full shadow-sm">
                <svg role="img" viewBox="0 0 24 24" className="w-3.5 h-3.5" style={{ fill: `#${siAnthropic.hex}` }}>
                  <path d={siAnthropic.path} />
                </svg>
                <span className="text-xs font-medium text-gray-700">Claude</span>
              </div>
              {/* Gemini */}
              <div className="flex items-center gap-2 bg-white border border-gray-200 px-3 py-1.5 rounded-full shadow-sm">
                <svg role="img" viewBox="0 0 24 24" className="w-3.5 h-3.5" style={{ fill: `#${siGooglegemini.hex}` }}>
                  <path d={siGooglegemini.path} />
                </svg>
                <span className="text-xs font-medium text-gray-700">Gemini</span>
              </div>
              {/* Perplexity */}
              <div className="flex items-center gap-2 bg-white border border-gray-200 px-3 py-1.5 rounded-full shadow-sm">
                <svg role="img" viewBox="0 0 24 24" className="w-3.5 h-3.5" style={{ fill: `#${siPerplexity.hex}` }}>
                  <path d={siPerplexity.path} />
                </svg>
                <span className="text-xs font-medium text-gray-700">Perplexity</span>
              </div>
              {/* Copilot - Using Bot icon from Lucide as fallback */}
              <div className="flex items-center gap-2 bg-white border border-gray-200 px-3 py-1.5 rounded-full shadow-sm">
                <Activity className="w-3.5 h-3.5 text-blue-600" />
                <span className="text-xs font-medium text-gray-700">Copilot</span>
              </div>
              {/* Grok (X) */}
              <div className="flex items-center gap-2 bg-white border border-gray-200 px-3 py-1.5 rounded-full shadow-sm">
                <svg role="img" viewBox="0 0 24 24" className="w-3.5 h-3.5" style={{ fill: `#${siX.hex}` }}>
                  <path d={siX.path} />
                </svg>
                <span className="text-xs font-medium text-gray-700">Grok</span>
              </div>
              {/* Meta AI */}
              <div className="flex items-center gap-2 bg-white border border-gray-200 px-3 py-1.5 rounded-full shadow-sm">
                <svg role="img" viewBox="0 0 24 24" className="w-3.5 h-3.5" style={{ fill: `#${siMeta.hex}` }}>
                  <path d={siMeta.path} />
                </svg>
                <span className="text-xs font-medium text-gray-700">Meta AI</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Problem Statement Section */}
      <div className="bg-white py-12 border-y border-gray-100">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-8">
              <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4">
                The way customers find brands is changing
              </h2>
              <p className="text-lg text-gray-600 max-w-3xl mx-auto">
                More consumers are turning to AI assistants for recommendations instead of traditional search. If you're not visible in AI responses, you're missing a growing channel.
              </p>
            </div>
            <MetricCardGroup>
              <MetricCard
                value="83"
                suffix="%"
                label="of consumers have used AI for product research in 2024"
                variant="danger"
              />
              <MetricCard
                value="4 in 10"
                label="shoppers trust AI recommendations as much as friends and family"
                variant="warning"
              />
              <MetricCard
                value="?"
                label="Most brands have no idea if AI is recommending them — or how to fix it"
                variant="primary"
              />
            </MetricCardGroup>

            {/* 2025 Prediction */}
            <div className="mt-12">
              <h3 className="text-xl font-bold text-gray-900 mb-6">2025 Prediction</h3>
              <MetricCardGroup>
                <MetricCard
                  value="60"
                  suffix="%+"
                  label="Searches Processed by AI"
                />
                <MetricCard
                  value="50"
                  suffix="%+"
                  label="Non-Text Discovery"
                />
                <MetricCard
                  value="100"
                  suffix="%"
                  label="First-Mover Advantage"
                />
              </MetricCardGroup>
              <p className="text-gray-600 mt-6">
                By end of 2025, <strong>60%+ of search queries will be processed by AI</strong> before users see traditional results.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Demo Callout Section */}
      <div className="bg-gradient-to-r from-purple-100 via-pink-50 to-indigo-100 py-12">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <div className="bg-white rounded-2xl shadow-2xl p-8 md:p-12">
              <div className="flex flex-col md:flex-row items-center gap-8">
                <div className="flex-1">
                  <div className="inline-flex items-center bg-purple-100 px-4 py-2 rounded-full mb-4">
                    <Eye className="h-5 w-5 mr-2 text-purple-700" />
                    <span className="text-sm font-semibold text-purple-700">Interactive Demo</span>
                  </div>
                  <h2 className="text-3xl font-bold text-gray-900 mb-4">
                    See BrandIndex AI in Action
                  </h2>
                  <p className="text-lg text-gray-600 mb-6">
                    Explore our fully-featured demo with real data from Acme Snacks. See competitive analysis,
                    market intelligence, search tracking, and more - all without creating an account.
                  </p>
                  <ul className="space-y-3 mb-6">
                    <li className="flex items-start">
                      <CheckCircle className="h-5 w-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" />
                      <span className="text-gray-700">Full dashboard with real analytics</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle className="h-5 w-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" />
                      <span className="text-gray-700">Interactive competitor comparisons</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle className="h-5 w-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" />
                      <span className="text-gray-700">Market intelligence insights</span>
                    </li>
                  </ul>
                  <Link
                    to="/demo"
                    className="w-full md:w-auto inline-flex items-center justify-center bg-gradient-to-r from-indigo-600 to-purple-600 text-white px-8 py-4 rounded-lg font-semibold text-lg hover:from-indigo-700 hover:to-purple-700 transition-all shadow-lg hover:shadow-xl group"
                  >
                    <Eye className="mr-2 h-5 w-5" />
                    Launch Demo
                    <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
                  </Link>
                </div>
                <div className="w-full md:w-auto flex-shrink-0">
                  <div className="bg-gradient-to-br from-purple-500 to-pink-500 rounded-2xl p-8 text-white shadow-2xl">
                    <div className="text-center">
                      <div className="text-6xl font-bold mb-2">73.5</div>
                      <div className="text-sm opacity-90 mb-4">Demo AI Score</div>
                      <div className="space-y-3 text-left">
                        <div className="flex items-center justify-between">
                          <span className="text-sm">ChatGPT</span>
                          <span className="font-semibold">85%</span>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-sm">Claude</span>
                          <span className="font-semibold">72%</span>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-sm">Gemini</span>
                          <span className="font-semibold">64%</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Key Features */}
      <div className="container mx-auto px-4 py-16">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Everything You Need to Dominate AI Visibility
            </h2>
            <p className="text-lg text-gray-600 max-w-4xl mx-auto">
              <strong>Step 1:</strong> We consume all available content — your brand's website, content library, competitor sites, and broader market intelligence. <strong>Step 2:</strong> Our AI evaluates both search behavior (how customers find solutions) and market positioning (how you compare to competitors). <strong>Step 3:</strong> You get brand rankings, unique positioning recommendations, and performance tracking to measure the impact of your strategies over time.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {/* Feature 1 */}
            <div className="bg-white rounded-xl p-6 shadow-lg hover:shadow-xl transition-all border-2 border-indigo-100">
              <div className="bg-indigo-100 w-12 h-12 rounded-lg flex items-center justify-center mb-4">
                <BarChart3 className="h-6 w-6 text-indigo-600" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">
                Know Exactly Where You Rank
              </h3>
              <p className="text-gray-600">
                Get a real-time AI Visibility Score (0-100) showing precisely how often AI mentions your brand vs. competitors. See breakdowns by provider, query type, and category.
              </p>
            </div>

            {/* Feature 2 */}
            <div className="bg-white rounded-xl p-6 shadow-lg hover:shadow-xl transition-all border-2 border-purple-100">
              <div className="bg-purple-100 w-12 h-12 rounded-lg flex items-center justify-center mb-4">
                <Search className="h-6 w-6 text-purple-600" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">
                See Every Question Customers Ask
              </h3>
              <p className="text-gray-600">
                Discover the exact queries customers use when searching for solutions like yours. Know which questions lead to your brand — and which ones don't.
              </p>
            </div>

            {/* Feature 3 */}
            <div className="bg-white rounded-xl p-6 shadow-lg hover:shadow-xl transition-all border-2 border-green-100">
              <div className="bg-green-100 w-12 h-12 rounded-lg flex items-center justify-center mb-4">
                <Users className="h-6 w-6 text-green-600" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">
                Discover Why Competitors Win
              </h3>
              <p className="text-gray-600">
                Head-to-head comparison reveals exactly why competitors get recommended over you. Identify content gaps and positioning opportunities they're missing.
              </p>
            </div>

            {/* Feature 4 */}
            <div className="bg-white rounded-xl p-6 shadow-lg hover:shadow-xl transition-all border-2 border-amber-100">
              <div className="bg-amber-100 w-12 h-12 rounded-lg flex items-center justify-center mb-4">
                <Target className="h-6 w-6 text-amber-600" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">
                Get Actionable Recommendations
              </h3>
              <p className="text-gray-600">
                AI-powered insights tell you exactly what to fix. Specific content suggestions, positioning strategies, and quick wins tailored to your market.
              </p>
            </div>

            {/* Feature 5 */}
            <div className="bg-white rounded-xl p-6 shadow-lg hover:shadow-xl transition-all border-2 border-blue-100">
              <div className="bg-blue-100 w-12 h-12 rounded-lg flex items-center justify-center mb-4">
                <TrendingUp className="h-6 w-6 text-blue-600" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">
                Track Your Progress Over Time
              </h3>
              <p className="text-gray-600">
                See your visibility improve week over week. Historical trends show what's working so you can double down on winning strategies.
              </p>
            </div>

            {/* Feature 6 */}
            <div className="bg-white rounded-xl p-6 shadow-lg hover:shadow-xl transition-all border-2 border-pink-100">
              <div className="bg-pink-100 w-12 h-12 rounded-lg flex items-center justify-center mb-4">
                <Zap className="h-6 w-6 text-pink-600" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">
                Optimize for AI Discovery
              </h3>
              <p className="text-gray-600">
                Generate LLMS.txt files and structured content that helps AI assistants understand and recommend your brand accurately.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Market Analysis Focus */}
      <div className="bg-gradient-to-br from-indigo-600 to-purple-600 py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto text-center text-white">
            <h2 className="text-3xl font-bold mb-6">
              From Market Analysis to Strategic Action
            </h2>
            <p className="text-xl mb-8 text-indigo-100">
              We don't just analyze — we help you understand where you truly stand, identify what makes you unique, and track the impact of your strategic positioning decisions over time.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 text-left">
              <div className="bg-white/10 backdrop-blur-sm rounded-lg p-6">
                <h3 className="font-semibold text-lg mb-2 flex items-center gap-2">
                  <BarChart3 className="h-5 w-5" />
                  Comprehensive Content Analysis
                </h3>
                <p className="text-indigo-100 text-sm">
                  Ingest content from your brand, competitors, and market leaders. Analyze search behavior, buying signals, and how consumers discover solutions across all channels.
                </p>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-lg p-6">
                <h3 className="font-semibold text-lg mb-2 flex items-center gap-2">
                  <Target className="h-5 w-5" />
                  Strategic Positioning Insights
                </h3>
                <p className="text-indigo-100 text-sm">
                  Get brand position rankings, competitive gap analysis, and AI-powered recommendations to differentiate uniquely in your market based on real data.
                </p>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-lg p-6">
                <h3 className="font-semibold text-lg mb-2 flex items-center gap-2">
                  <TrendingUp className="h-5 w-5" />
                  Impact Measurement
                </h3>
                <p className="text-indigo-100 text-sm">
                  Track rankings over time to see exactly how your positioning strategies perform. Measure ROI on content, campaigns, and market positioning initiatives.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Benefits Section */}
      <div className="bg-white py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-4xl md:text-3xl font-bold md:text-center mb-12 text-gray-900">
              Why Choose BrandIndex AI?
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div className="flex items-start">
                <CheckCircle className="h-6 w-6 text-green-500 mr-4 mt-1 flex-shrink-0" />
                <div>
                  <h3 className="text-xl font-semibold mb-2 text-gray-900">Comprehensive Market Analysis</h3>
                  <p className="text-gray-600">
                    We consume and analyze your entire market ecosystem — your brand content, all competitor materials, and broader industry intelligence — to give you complete visibility into your competitive landscape.
                  </p>
                </div>
              </div>
              <div className="flex items-start">
                <CheckCircle className="h-6 w-6 text-green-500 mr-4 mt-1 flex-shrink-0" />
                <div>
                  <h3 className="text-xl font-semibold mb-2 text-gray-900">Data-Driven Differentiation</h3>
                  <p className="text-gray-600">
                    Get strategic recommendations based on real market data to position your brand uniquely. Identify gaps competitors aren't filling and opportunities to stand out in both search and market positioning.
                  </p>
                </div>
              </div>
              <div className="flex items-start">
                <CheckCircle className="h-6 w-6 text-green-500 mr-4 mt-1 flex-shrink-0" />
                <div>
                  <h3 className="text-xl font-semibold mb-2 text-gray-900">Measurable Impact Tracking</h3>
                  <p className="text-gray-600">
                    Monitor your brand position rankings over time to see exactly how your strategies perform. Track changes month over month, measure ROI on positioning initiatives, and make confident data-driven decisions.
                  </p>
                </div>
              </div>
              <div className="flex items-start">
                <CheckCircle className="h-6 w-6 text-green-500 mr-4 mt-1 flex-shrink-0" />
                <div>
                  <h3 className="text-xl font-semibold mb-2 text-gray-900">AI-Powered Competitive Intelligence</h3>
                  <p className="text-gray-600">
                    Uncover what makes competitors successful, where they're vulnerable, and where white space exists in your market. Leverage AI to analyze thousands of data points and surface actionable competitive insights.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Who It's For Section */}
      <div className="bg-gradient-to-br from-gray-50 to-indigo-50 py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">
                Built for Brands That Want to Be Found
              </h2>
              <p className="text-lg text-gray-600">
                Whether you're a local business or a national brand, BrandIndex AI helps you understand and improve your AI visibility.
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <div className="bg-white rounded-xl p-6 shadow-md hover:shadow-lg transition-all text-center">
                <div className="bg-indigo-100 w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Target className="h-7 w-7 text-indigo-600" />
                </div>
                <h3 className="font-semibold text-gray-900 mb-2">Brand Managers</h3>
                <p className="text-sm text-gray-600">
                  Track brand perception across AI platforms and prove the impact of your strategies.
                </p>
              </div>
              <div className="bg-white rounded-xl p-6 shadow-md hover:shadow-lg transition-all text-center">
                <div className="bg-purple-100 w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-4">
                  <TrendingUp className="h-7 w-7 text-purple-600" />
                </div>
                <h3 className="font-semibold text-gray-900 mb-2">Marketing Teams</h3>
                <p className="text-sm text-gray-600">
                  Optimize content for AI discovery and show leadership the results.
                </p>
              </div>
              <div className="bg-white rounded-xl p-6 shadow-md hover:shadow-lg transition-all text-center">
                <div className="bg-green-100 w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Users className="h-7 w-7 text-green-600" />
                </div>
                <h3 className="font-semibold text-gray-900 mb-2">Agencies</h3>
                <p className="text-sm text-gray-600">
                  Manage multiple client brands and deliver AI visibility reports they'll love.
                </p>
              </div>
              <div className="bg-white rounded-xl p-6 shadow-md hover:shadow-lg transition-all text-center">
                <div className="bg-amber-100 w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Shield className="h-7 w-7 text-amber-600" />
                </div>
                <h3 className="font-semibold text-gray-900 mb-2">Local Businesses</h3>
                <p className="text-sm text-gray-600">
                  Get found when customers ask AI for local recommendations in your area.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Pricing Section */}
      <div className="container mx-auto px-4 py-16">
        <div className="max-w-5xl mx-auto space-y-8">
          <div className="md:text-center">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Simple, Transparent Pricing
            </h2>
            <p className="text-lg text-gray-600 mb-2">
              Choose the plan that matches your brand's visibility needs
            </p>
            <p className="text-sm text-gray-500">
              Start with a free 7-day trial • No credit card required
            </p>
          </div>

          {/* Plan Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* Starter Plan */}
            <div className="border-2 border-green-200 rounded-xl p-6 hover:border-green-400 transition-colors bg-white shadow-lg">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center space-x-2">
                  <Activity className="h-6 w-6 text-green-600" />
                  <h3 className="text-2xl font-bold text-gray-900">Starter</h3>
                </div>
                <span className="px-3 py-1 bg-green-100 text-green-700 text-xs font-semibold rounded-full">
                  Monthly
                </span>
              </div>
              <div className="mb-6">
                <span className="text-5xl font-bold text-gray-900">$15</span>
                <span className="text-gray-600 ml-2">/month</span>
              </div>
              <ul className="space-y-3 mb-8">
                <li className="flex items-start">
                  <CheckCircle className="h-5 w-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700">10 search queries</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="h-5 w-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700">3 competitor tracking</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="h-5 w-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700">Monthly analysis</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="h-5 w-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700">GPT & Claude providers</span>
                </li>
              </ul>
              <Link
                to="/register"
                className="block w-full text-center px-6 py-3 bg-green-600 text-white rounded-lg font-semibold hover:bg-green-700 transition-colors"
              >
                Start Free Trial
              </Link>
            </div>

            {/* Growth Plan */}
            <div className="border-2 border-blue-400 rounded-xl p-6 hover:border-blue-500 transition-colors bg-white shadow-xl relative">
              <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                <span className="bg-blue-600 text-white text-xs font-bold px-4 py-1 rounded-full shadow-lg">
                  BEST VALUE
                </span>
              </div>
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center space-x-2">
                  <TrendingUp className="h-6 w-6 text-blue-600" />
                  <h3 className="text-2xl font-bold text-gray-900">Growth</h3>
                </div>
                <span className="px-3 py-1 bg-blue-100 text-blue-700 text-xs font-semibold rounded-full">
                  Monthly
                </span>
              </div>
              <div className="mb-6">
                <span className="text-5xl font-bold text-gray-900">$29</span>
                <span className="text-gray-600 ml-2">/month</span>
              </div>
              <ul className="space-y-3 mb-8">
                <li className="flex items-start">
                  <CheckCircle className="h-5 w-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700">30 search queries</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="h-5 w-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700">6 competitor tracking</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="h-5 w-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700">Insights & Recommendations</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="h-5 w-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700">All AI providers</span>
                </li>
              </ul>
              <Link
                to="/register"
                className="block w-full text-center px-6 py-3 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 transition-colors"
              >
                Start Free Trial
              </Link>
            </div>

            {/* Pro Plan */}
            <div className="border-2 border-purple-200 rounded-xl p-6 hover:border-purple-400 transition-colors bg-white shadow-lg">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center space-x-2">
                  <Zap className="h-6 w-6 text-purple-600" />
                  <h3 className="text-2xl font-bold text-gray-900">Pro</h3>
                </div>
                <span className="px-3 py-1 bg-purple-100 text-purple-700 text-xs font-semibold rounded-full">
                  Weekly
                </span>
              </div>
              <div className="mb-6">
                <span className="text-5xl font-bold text-gray-900">$69</span>
                <span className="text-gray-600 ml-2">/month</span>
              </div>
              <ul className="space-y-3 mb-8">
                <li className="flex items-start">
                  <CheckCircle className="h-5 w-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700">75 search queries</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="h-5 w-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700">9 competitor tracking</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="h-5 w-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700">Weekly analysis</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="h-5 w-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700">All features included</span>
                </li>
              </ul>
              <Link
                to="/register"
                className="block w-full text-center px-6 py-3 bg-purple-600 text-white rounded-lg font-semibold hover:bg-purple-700 transition-colors"
              >
                Start Free Trial
              </Link>
            </div>
          </div>

          {/* Shopify Callout */}
          <div className="bg-gradient-to-r from-green-50 to-emerald-50 border-2 border-green-200 rounded-2xl p-6 md:p-8">
            <div className="flex flex-col md:flex-row items-center gap-6">
              <div className="flex-shrink-0">
                <div className="bg-white rounded-2xl p-4 shadow-md border border-green-200">
                  <img
                    src="https://cdn.shopify.com/shopifycloud/brochure/assets/brand-assets/shopify-logo-primary-logo-456baa801ee66a0a435671082365958316831c9960c480451dd0330bcdae304f.svg"
                    alt="Shopify"
                    className="w-32 h-auto"
                  />
                </div>
              </div>
              <div className="flex-1 text-center md:text-left">
                <div className="inline-flex items-center bg-green-100 px-3 py-1 rounded-full mb-2">
                  <span className="text-xs font-semibold text-green-700">SHOPIFY MERCHANTS</span>
                </div>
                <h3 className="text-xl md:text-2xl font-bold text-gray-900 mb-2">
                  Running a Shopify Store?
                </h3>
                <p className="text-gray-600 mb-4">
                  Install our official Shopify app for seamless integration. Your products sync automatically,
                  and you get AI visibility tracking built right into your Shopify admin.
                </p>
                <div className="flex flex-col sm:flex-row items-center gap-3">
                  <a
                    href="https://apps.shopify.com/ai-brandindex"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center bg-[#96bf48] hover:bg-[#7aa03d] text-white px-6 py-3 rounded-lg font-semibold transition-colors shadow-md hover:shadow-lg"
                  >
                    Install Shopify App
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </a>
                  <span className="text-sm text-gray-500">
                    Same features, native Shopify experience
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Features List */}
          <div className="bg-white rounded-xl shadow-lg p-8">
            <h3 className="text-xl font-bold text-gray-900 mb-6 text-center">
              Every plan includes
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="flex items-start">
                <CheckCircle className="h-5 w-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" />
                <span className="text-gray-700">AI BrandIndex Score tracking</span>
              </div>
              <div className="flex items-start">
                <CheckCircle className="h-5 w-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" />
                <span className="text-gray-700">Search Intelligence & Query Tracking</span>
              </div>
              <div className="flex items-start">
                <CheckCircle className="h-5 w-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" />
                <span className="text-gray-700">Market Insights Dashboard</span>
              </div>
              <div className="flex items-start">
                <CheckCircle className="h-5 w-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" />
                <span className="text-gray-700">Competitive Analysis</span>
              </div>
              <div className="flex items-start">
                <CheckCircle className="h-5 w-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" />
                <span className="text-gray-700">LLMS.txt Generator</span>
              </div>
              <div className="flex items-start">
                <CheckCircle className="h-5 w-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" />
                <span className="text-gray-700">Historical Analytics & Trends</span>
              </div>
            </div>

            <div className="mt-6 pt-6 border-t border-gray-200">
              <h4 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-4 text-center">
                Growth & Pro plans add
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="flex items-start">
                  <TrendingUp className="h-5 w-5 text-blue-500 mr-3 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700">Smart Insights & Recommendations</span>
                </div>
                <div className="flex items-start">
                  <TrendingUp className="h-5 w-5 text-blue-500 mr-3 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700">All AI providers (GPT, Claude, Gemini, Perplexity, etc.)</span>
                </div>
              </div>
            </div>
          </div>

          {/* CTA */}
          <div className="text-center">
            <Link
              to="/register"
              className="inline-flex items-center bg-gradient-to-r from-indigo-500 to-purple-500 text-white px-8 py-4 rounded-lg font-semibold text-lg hover:from-indigo-600 hover:to-purple-600 transition-all shadow-lg hover:shadow-xl"
            >
              Start Your Free Trial
              <ArrowRight className="ml-2 h-5 w-5" />
            </Link>
            <p className="text-sm text-gray-500 mt-4">
              No credit card required • Cancel anytime
            </p>
          </div>
        </div>
      </div>

      {/* FAQ Section */}
      <div className="bg-gray-50 py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">
                Frequently Asked Questions
              </h2>
              <p className="text-lg text-gray-600">
                Everything you need to know about AI visibility tracking
              </p>
            </div>
            <div className="space-y-6">
              <div className="bg-white rounded-xl p-6 shadow-md">
                <h3 className="font-semibold text-gray-900 mb-2">
                  How is this different from SEO tools?
                </h3>
                <p className="text-gray-600">
                  SEO tools track your visibility in traditional search results. BrandIndex AI tracks how AI assistants like ChatGPT, Claude, and Gemini respond when customers ask for recommendations. It's a completely different channel that most brands aren't monitoring yet.
                </p>
              </div>
              <div className="bg-white rounded-xl p-6 shadow-md">
                <h3 className="font-semibold text-gray-900 mb-2">
                  Do I need to hire a consultant or agency?
                </h3>
                <p className="text-gray-600">
                  No. BrandIndex AI is fully automated and self-service. You get actionable insights without expensive consulting fees or sales calls. Just sign up, add your brand, and start tracking. Our AI does the heavy lifting.
                </p>
              </div>
              <div className="bg-white rounded-xl p-6 shadow-md">
                <h3 className="font-semibold text-gray-900 mb-2">
                  Does this work for local businesses?
                </h3>
                <p className="text-gray-600">
                  Absolutely! We built BrandIndex AI with local businesses in mind. You can set your service area and track how AI responds to local queries like "best plumber in Austin" or "top restaurants near me." Local AI visibility is increasingly important as consumers use AI for local recommendations.
                </p>
              </div>
              <div className="bg-white rounded-xl p-6 shadow-md">
                <h3 className="font-semibold text-gray-900 mb-2">
                  How quickly will I see results?
                </h3>
                <p className="text-gray-600">
                  You'll see your current AI visibility status immediately after onboarding. Improving your visibility depends on the changes you make based on our recommendations — some brands see improvements in weeks, while others take longer depending on their market and competition.
                </p>
              </div>
              <div className="bg-white rounded-xl p-6 shadow-md">
                <h3 className="font-semibold text-gray-900 mb-2">
                  What's included in the $15/month Starter plan?
                </h3>
                <p className="text-gray-600">
                  Everything you need to get started: AI visibility tracking across major providers, 10 search queries, 3 competitor tracking, monthly analysis reports, and our LLMS.txt generator. Upgrade anytime if you need more queries or weekly analysis.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Latest from the Blog */}
      <div className="bg-white py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-10">
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <BookOpen className="h-6 w-6 text-indigo-600" />
                  <h2 className="text-3xl font-bold text-gray-900">
                    Latest from the Blog
                  </h2>
                </div>
                <p className="text-gray-600">
                  AI visibility strategies, tips, and insights for your business
                </p>
              </div>
              <Link
                to="/blog"
                className="mt-4 md:mt-0 inline-flex items-center text-indigo-600 font-semibold hover:text-indigo-700 transition-colors group"
              >
                View All Articles
                <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
              </Link>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {latestPosts.map((post) => (
                <Link
                  key={post.slug}
                  to={`/blog/${post.slug}`}
                  className="group"
                >
                  <article className="h-full bg-gray-50 rounded-xl p-6 border border-gray-200 hover:border-indigo-500 hover:shadow-lg transition-all">
                    {/* Category Badge */}
                    <div className="mb-3">
                      <span className="inline-block px-3 py-1 bg-indigo-100 text-indigo-700 text-xs font-medium rounded-full">
                        {post.category}
                      </span>
                    </div>

                    {/* Title */}
                    <h3 className="text-lg font-bold text-gray-900 group-hover:text-indigo-600 mb-3 line-clamp-2">
                      {post.title}
                    </h3>

                    {/* Description */}
                    <p className="text-gray-600 text-sm mb-4 line-clamp-2">
                      {post.description}
                    </p>

                    {/* Meta Info */}
                    <div className="flex items-center gap-4 text-xs text-gray-500">
                      <div className="flex items-center gap-1">
                        <Calendar className="w-3 h-3" />
                        <time dateTime={post.publishDate}>{formatDate(post.publishDate)}</time>
                      </div>
                    </div>

                    {/* Read More Link */}
                    <div className="mt-4 flex items-center gap-2 text-indigo-600 font-medium text-sm group-hover:gap-3 transition-all">
                      Read Article <ArrowRight className="w-4 h-4" />
                    </div>
                  </article>
                </Link>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* CTA Section */}
      <div className="bg-gray-900 text-white py-16 pb-32">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl font-bold mb-4">
              Have questions before you get started?
            </h2>
            <p className="text-xl text-gray-300 mb-8">
              Our team is here to help. Reach out and we'll get back to you quickly.
            </p>
            <button
              onClick={() => setIsContactModalOpen(true)}
              className="inline-flex items-center bg-gradient-to-r from-indigo-500 to-purple-500 text-white px-8 py-4 rounded-lg font-semibold text-lg hover:from-indigo-600 hover:to-purple-600 transition-all shadow-lg hover:shadow-xl"
            >
              <Mail className="mr-2 h-5 w-5" />
              Contact us
            </button>
          </div>
        </div>
      </div>

      {/* Sticky Bottom Bar */}
      <div
        className={`fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 shadow-lg z-50 transition-transform duration-300 ${
          showStickyBar ? 'translate-y-0' : 'translate-y-full'
        }`}
      >
        <div className="container mx-auto px-4 py-3">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-3 sm:gap-0">
            <div className="flex items-center">
              <Shield className="h-5 w-5 text-indigo-600 mr-2" />
              <span className="text-sm font-medium text-gray-900">
                Start your free 7-day trial today
              </span>
            </div>
            <div className="flex items-center gap-4 sm:gap-6">
              <Link
                to="/login"
                className="text-indigo-600 font-medium text-sm hover:text-indigo-700"
              >
                Sign In
              </Link>
              <Link
                to="/register"
                className="bg-gradient-to-r from-indigo-500 to-purple-500 text-white px-6 py-2 rounded-lg font-semibold text-sm hover:from-indigo-600 hover:to-purple-600 transition-all whitespace-nowrap"
              >
                Get Started Free
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* Contact Form Modal */}
      <ContactFormModal
        isOpen={isContactModalOpen}
        onClose={() => setIsContactModalOpen(false)}
      />
    </div>
    </>
  )
}
